import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FunnelLinks from "@/components/FunnelLinks";
import NewsletterSignup from "@/components/NewsletterSignup";
import CalcBanner from "@/components/CalcBanner";
import { Flame } from "lucide-react";
import { api, imgUrl } from "@/lib/api";

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    document.title = "Blog o zateplení — rady, dotácie, materiály | Zateplenia.eu";
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/blog/popular?limit=5").then((r) => setPopular(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Header />
      <section className="pt-36 pb-16 max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Blog</p>
        <h1 className="font-display text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
          Rady o <span className="text-gold">zateplení</span>
        </h1>
        <p className="text-white/60 text-lg max-w-2xl mt-6">Dotácie Obnov dom, výber materiálu, hrúbky a ceny — praktické články pre váš projekt.</p>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 grid grid-cols-1 lg:grid-cols-4 gap-10">
        <div className="lg:col-span-3">
          {posts.length === 0 && <p className="text-white/40 border border-white/10 p-10 text-center">Zatiaľ žiadne články.</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {posts.map((p, i) => (
              <motion.article key={p.slug} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }} className="group border border-white/10 bg-ink-2 overflow-hidden">
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={imgUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                    <span className="absolute top-4 left-4 bg-gold text-ink text-xs font-bold uppercase tracking-wider px-3 py-1">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="text-white/40 text-xs font-mono2 mb-2">{new Date(p.created_at).toLocaleDateString("sk-SK")}</div>
                    <h2 className="font-display text-2xl text-white leading-tight mb-2 group-hover:text-gold transition-colors">{p.title}</h2>
                    <p className="text-white/55 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="text-gold text-sm mt-4 flex items-center gap-1">Čítať článok <ArrowRight className="w-4 h-4" /></div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
          <div className="mt-10"><CalcBanner context="blog" /></div>
        </div>

        {/* Sidebar: most-read + newsletter */}
        <aside className="lg:col-span-1 space-y-8">
          {popular.length > 0 && (
            <div className="border border-white/10 bg-ink-2 p-6" data-testid="popular-articles">
              <div className="flex items-center gap-2 text-gold mb-5">
                <Flame className="w-4 h-4" />
                <p className="font-mono2 uppercase tracking-[0.25em] text-xs">Najčítanejšie</p>
              </div>
              <ol className="space-y-4">
                {popular.map((p, i) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`} data-testid={`popular-${p.slug}`} className="group flex gap-3">
                      <span className="font-display text-2xl text-gold/50 leading-none w-6 shrink-0">{i + 1}</span>
                      <span className="text-white/75 text-sm leading-snug group-hover:text-gold transition-colors">{p.title}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <NewsletterSignup source="blog-list" />
        </aside>
      </section>

      <section className="bg-ink-2 py-20 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-6">Začnite tu</p>
          <FunnelLinks from="blog-list" />
        </div>
      </section>
      <Footer />
    </div>
  );
}
